export default class TooltipCursor {
    /**
     * Initializes a new instance of the TooltipCursor.
     *
     * @param {object} [options] - Configuration options for the tooltip.
     * @param {string} [options.triggerSelector='.cursor-tooltip-trigger'] - CSS selector for trigger elements.
     * @param {string} [options.tooltipClass='cursor-tooltip'] - CSS class for the tooltip element.
     * @param {string} [options.label='Zoom in'] - Default text for the tooltip.
     */
    constructor(options) {
        const defaultOptions = {
            triggerSelector: '.cursor-tooltip-trigger',
            tooltipClass: 'cursor-tooltip',
            label: 'Zoom in'
        };

        // Merge default options with user-provided options.
        this.options = { ...defaultOptions, ...options };

        /**
         * Stores active tooltips, mapping trigger elements to their tooltip elements and
         * associated mouse movement handlers. Using WeakMap allows for garbage collection
         * of entries when the trigger element is no longer referenced.
         */
        this.activeTooltips = new WeakMap();

        /**
         * @type {number | null}
         * Stores the requestAnimationFrame ID for the tooltip's position update loop,
         * allowing it to be cancelled when the tooltip is no longer active.
         */
        this.raf = null;

        this.init();
    }

    /**
     * Initializes the tooltip functionality by attaching event listeners to trigger elements.
     * Uses a delegated event listener approach for better performance on dynamic content
     * if many triggers are present, but for simplicity and directness, current approach
     * of attaching to each is kept as per original request to not change logic significantly.
     */
    init() {
        // Use `Array.from` for robust iteration over NodeList.
        Array.from(document.querySelectorAll(this.options.triggerSelector)).forEach(el => {
            el.addEventListener('mouseenter', this.handleMouseEnter);
            el.addEventListener('mouseleave', this.handleMouseLeave);
        });
    }

    /**
     * Creates and appends a tooltip element to the document body.
     *
     * @param {MouseEvent} e - The mouse event that triggered the tooltip creation.
     * @param {string} content - The text content to display in the tooltip.
     * @returns {HTMLElement} The created tooltip element.
     */
    createTooltipElement(e, content) {
        const tooltip = document.createElement('div');
        tooltip.className = this.options.tooltipClass;
        tooltip.textContent = content;

        // Set initial styles for the tooltip's entrance animation.
        tooltip.style.transformOrigin = 'top left';
        tooltip.style.transition = 'transform 0.25s ease-out';
        tooltip.style.transform = `
            translate(${e.clientX}px, ${e.clientY}px)
            rotateX(-90deg)
            perspective(100px)
        `;
        document.body.appendChild(tooltip);

        // Force reflow to ensure the initial transform is applied before the transition starts.
        // This is a common technique to trigger CSS transitions immediately after element creation.
        void tooltip.offsetHeight;

        return tooltip;
    }

    /**
     * Handles the mouseenter event on a trigger element.
     * Creates and displays a tooltip, then sets up its movement tracking.
     *
     * @param {MouseEvent} e - The mouseenter event.
     */
    handleMouseEnter = (e) => { // Using arrow function to autobind 'this'
        const trigger = e.currentTarget;

        // Check if a tooltip already exists for this trigger to prevent duplicates.
        if (this.activeTooltips.has(trigger)) {
            // If it exists, use the existing instance. No new tooltip element is created.
            return;
        }

        const label = trigger.getAttribute('data-label') || this.options.label;
        const tooltip = this.createTooltipElement(e, label);

        let targetX = e.clientX;
        let targetY = e.clientY;
        let currentX = targetX;
        let currentY = targetY;
        let lastX = targetX;
        let lastY = targetY;
        let velocityX = 0;
        let velocityY = 0;
        const maxZ = 20;
        const minZ = -20;

        /**
         * Updates the tooltip's position and rotation based on cursor movement.
         * This function is called on each animation frame for smooth updates.
         */
        const updateTooltipPosition = () => {
            velocityX = targetX - lastX;
            velocityY = targetY - lastY;
            lastX = targetX;
            lastY = targetY;

            // Linear interpolation (lerp) for smoother movement.
            // This reduces the "snappiness" of the tooltip's follow behavior.
            currentX += (targetX - currentX) * 0.2;
            currentY += (targetY - currentY) * 0.2;

            // Calculate rotation based on vertical velocity for a dynamic effect.
            const rotateZ = Math.max(minZ, Math.min(maxZ, velocityY * 3));

            tooltip.style.transform = `
                translate(${currentX}px, ${currentY}px)
                rotateX(0deg)
                rotateZ(${rotateZ}deg)
                perspective(100px)
            `;

            this.raf = requestAnimationFrame(updateTooltipPosition);
        };

        /**
         * Handles pointermove events to update the target position of the tooltip.
         * Also checks if the pointer has moved off the trigger element.
         *
         * @param {PointerEvent} evt - The pointermove event.
         */
        const moveTooltip = (evt) => {
            // Check if the element under the pointer is still the trigger or one of its descendants.
            // This helps in cases where the mouse might move rapidly off the element.
            if (!trigger.contains(document.elementFromPoint(evt.clientX, evt.clientY))) {
                // If not, treat it as a mouse leave event.
                this.handleMouseLeave({
                    currentTarget: trigger, // Simulate a mouseleave event object.
                    clientX: evt.clientX, // Pass clientX/Y for accurate exit animation.
                    clientY: evt.clientY
                });
                return;
            }
            targetX = evt.clientX;
            targetY = evt.clientY;
        };

        // Attach the pointermove listener to the document for global tracking.
        document.addEventListener('pointermove', moveTooltip);

        // Store the tooltip element and its associated move handler in the WeakMap.
        // This allows us to retrieve and clean up these references later.
        this.activeTooltips.set(trigger, {
            tooltip,
            moveTooltip
        });

        // Start the animation loop for the tooltip's position.
        updateTooltipPosition();
    };

    /**
     * Handles the mouseleave event on a trigger element.
     * Hides and removes the tooltip, and cleans up event listeners and animation frames.
     *
     * @param {MouseEvent} e - The mouseleave event.
     */
    handleMouseLeave = (e) => { // Using arrow function to autobind 'this'
        const trigger = e.currentTarget;
        const state = this.activeTooltips.get(trigger);

        // If no active tooltip state is found for this trigger, exit.
        if (!state) {
            return;
        }

        const { tooltip, moveTooltip } = state;

        /**
         * Animates the tooltip out and then removes it from the DOM.
         */
        const animateOut = () => {
            // Cancel any ongoing animation frame requests for this tooltip.
            cancelAnimationFrame(this.raf);
            this.raf = null; // Reset raf ID.

            // Get the final position for the tooltip to animate out from the cursor's last position.
            // Use event's clientX/Y if available (e.g., from handleMouseEnter's simulated event),
            // otherwise fall back to the tooltip's current position to avoid jumps.
            const currentTransformMatch = tooltip.style.transform.match(/translate\(([-\d.]+)px, ([-\d.]+)px\)/);
            const finalX = e.clientX || (currentTransformMatch ? parseFloat(currentTransformMatch[1]) : 0);
            const finalY = e.clientY || (currentTransformMatch ? parseFloat(currentTransformMatch[2]) : 0);


            // Animate out the tooltip by rotating it back.
            tooltip.style.transform = `
                translate(${finalX}px, ${finalY}px)
                rotateX(-90deg)
                perspective(100px)
            `;

            // Remove the tooltip from the DOM after the animation completes.
            // The duration (300ms) should match the CSS transition duration.
            setTimeout(() => {
                tooltip.remove();
            }, 300);
        };

        // Remove the pointermove listener to stop tracking.
        document.removeEventListener('pointermove', moveTooltip);

        // Remove the tooltip's state from the WeakMap.
        this.activeTooltips.delete(trigger);

        // Start the tooltip exit animation.
        animateOut();
    };
}
